import React, { useRef, useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Product } from '@/types/product';
import ProductCard from './ProductCard';

type ProductVariant = Product['variants'][0];

interface ProductCarouselProps {
  products: Product[];
  title?: string;
  className?: string;
  onAddToCart?: (product: Product, variant: ProductVariant) => void;
  onQuickView?: (product: Product) => void;
}

export const ProductCarousel: React.FC<ProductCarouselProps> = ({
  products,
  title,
  className = '',
  onAddToCart,
  onQuickView
}) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);
  const [isMobile, setIsMobile] = useState(false);

  // Mobile detection
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const scroll = (direction: 'left' | 'right') => {
    if (scrollRef.current) {
      // Responsive scroll amounts
      const mobileScrollAmount = 280; // Slightly less than card width for mobile
      const desktopScrollAmount = 320; // Full card width + gap for desktop
      const scrollAmount = isMobile ? mobileScrollAmount : desktopScrollAmount;
      
      const newScrollLeft = scrollRef.current.scrollLeft + 
        (direction === 'left' ? -scrollAmount : scrollAmount);
      
      scrollRef.current.scrollTo({
        left: newScrollLeft,
        behavior: 'smooth'
      });
    }
  };

  const handleScroll = () => {
    if (scrollRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = scrollRef.current;
      setCanScrollLeft(scrollLeft > 5); // Small threshold for better UX
      setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 5);
    }
  };

  // Initialize scroll detection
  useEffect(() => {
    if (scrollRef.current) {
      handleScroll();
    }
  }, [products]);

  const handleAddToCart = (product: Product, variant: ProductVariant) => {
    if (onAddToCart) {
      onAddToCart(product, variant);
    } else {
      console.log('Add to cart:', product.name, variant);
    }
  };

  const handleQuickView = (product: Product) => {
    if (onQuickView) {
      onQuickView(product);
    } else {
      console.log('Quick view:', product.name);
    }
  };

  if (!products || products.length === 0) {
    return null;
  }

  return (
    <div className={`relative ${className}`}>
      {title && (
        <h3 className="text-xl md:text-2xl font-bold text-white mb-4 md:mb-6 px-4">
          {title}
        </h3>
      )}
      
      <div className="relative group">
        {/* Desktop Navigation Arrows */}
        {!isMobile && (
          <>
            <Button
              variant="secondary"
              size="sm"
              className={`absolute left-2 top-1/2 -translate-y-1/2 z-10 w-10 h-10 rounded-full bg-black/70 hover:bg-lime-400 text-white hover:text-black border-0 shadow-lg transition-all duration-200 ${
                !canScrollLeft ? 'opacity-0 pointer-events-none' : 'opacity-0 group-hover:opacity-100'
              }`}
              onClick={() => scroll('left')}
            >
              <ChevronLeft className="w-5 h-5" />
            </Button>

            <Button
              variant="secondary"
              size="sm"
              className={`absolute right-2 top-1/2 -translate-y-1/2 z-10 w-10 h-10 rounded-full bg-black/70 hover:bg-lime-400 text-white hover:text-black border-0 shadow-lg transition-all duration-200 ${
                !canScrollRight ? 'opacity-0 pointer-events-none' : 'opacity-0 group-hover:opacity-100'
              }`}
              onClick={() => scroll('right')}
            >
              <ChevronRight className="w-5 h-5" />
            </Button>
          </>
        )}

        {/* Products Container */}
        <div
          ref={scrollRef}
          className={`flex gap-3 md:gap-4 overflow-x-auto scroll-smooth px-4 ${
            isMobile 
              ? 'snap-x snap-mandatory scrollbar-hide' 
              : 'scrollbar-hide'
          }`}
          onScroll={handleScroll}
          style={{
            scrollbarWidth: 'none',
            msOverflowStyle: 'none'
          } as React.CSSProperties}
        >
          {products.map((product) => (
            <motion.div
              key={product.id}
              className={`flex-shrink-0 ${
                isMobile 
                  ? 'w-72 snap-start' // Mobile: slightly smaller, with snap
                  : 'w-80' // Desktop: full width
              }`}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              viewport={{ once: true }}
            >
              <ProductCard
                product={product}
                onAddToCart={handleAddToCart}
                onQuickView={handleQuickView}
                className="h-full"
              />
            </motion.div>
          ))}
          
          {/* Mobile end spacing */}
          {isMobile && <div className="w-4 flex-shrink-0" />}
        </div>

        {/* Mobile scroll indicators */}
        {isMobile && products.length > 1 && (
          <div className="flex justify-center mt-4 gap-1">
            {Array.from({ length: Math.min(5, Math.ceil(products.length / 1.5)) }).map((_, index) => (
              <div
                key={index}
                className="w-1.5 h-1.5 rounded-full bg-gray-600 transition-colors"
              />
            ))}
          </div>
        )}

        {/* Desktop scroll hint */}
        {!isMobile && canScrollRight && (
          <div className="absolute -bottom-2 right-4 text-xs text-gray-500 opacity-70">
            Scroll for more →
          </div>
        )}
      </div>
    </div>
  );
};

export default ProductCarousel; 